import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { login, verifyMfaCode, resendMfaCode } from "../services/api";
import { OrbitMark } from "../components/OrbitMark";
import { ThemeToggle } from "../components/ThemeToggle";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Second step: the console asked for the code it emailed. The password is
  // already accepted at this point — the session cookies exist but can't do
  // anything until this clears.
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  // Set by forceLogout() when an idle session was ended for you.
  const [expired] = useState(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("expired")
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { mfaRequired } = await login(email, password);
      if (mfaRequired) {
        setAwaitingCode(true);
        setNotice(`We've emailed a 6-digit code to ${email.trim()}. It expires in 10 minutes.`);
        return;
      }
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message ?? "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      await verifyMfaCode(code);
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message ?? "That code wasn't accepted.");
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    setError(null);
    setNotice(null);
    try {
      await resendMfaCode();
      setNotice("A new code is on its way.");
    } catch (err: any) {
      setError(err.message ?? "Could not send a new code.");
    }
  }

  return (
    <div className="login-page">
      <div style={{ position: "fixed", top: 20, right: 20, zIndex: 10 }}>
        <ThemeToggle />
      </div>
      <div className="login-card">
        <div className="login-brand">
          <span className="brand-mark"><OrbitMark size={20} /></span>
          <span className="brand-word">Orbit SA <small>Platform Admin</small></span>
        </div>
        <h1>Sign in</h1>
        <p className="muted">Internal back-office only.</p>

        {awaitingCode ? (
          <form className="form" onSubmit={onSubmitCode} noValidate>
            {notice && (
              <div
                className="login-notice"
                style={{
                  background: "var(--surface-2, rgba(125,125,125,0.12))",
                  border: "1px solid var(--border, rgba(125,125,125,0.25))",
                  color: "var(--text-dim)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13
                }}
              >
                {notice}
              </div>
            )}
            {error && <div className="login-error">{error}</div>}
            <label>
              Sign-in code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\\d{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                style={{ letterSpacing: 8, fontSize: 20, textAlign: "center" }}
              />
            </label>
            <button className="btn btn-primary" type="submit" disabled={loading || code.length !== 6} style={{ marginTop: 4 }}>
              {loading ? <><Loader2 size={16} className="spin" /> Checking…</> : "Verify and sign in"}
            </button>
            <button
              type="button"
              className="linklike"
              onClick={onResend}
              style={{ marginTop: 2, background: "none", border: "none", color: "var(--text-dim)", fontSize: 12, cursor: "pointer" }}
            >
              Email me a new code
            </button>
            <div className="row" style={{ gap: 8, color: "var(--text-mute)", fontSize: 12, marginTop: 6 }}>
              <ShieldCheck size={14} /> We'll remember this device for 30 days
            </div>
          </form>
        ) : (
        <form className="form" onSubmit={onSubmit} noValidate>
          {expired && !error && (
            <div
              className="login-notice"
              style={{
                background: "var(--surface-2, rgba(125,125,125,0.12))",
                border: "1px solid var(--border, rgba(125,125,125,0.25))",
                color: "var(--text-dim)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 13
              }}
            >
              Your session timed out. Please sign in again.
            </div>
          )}
          {error && <div className="login-error">{error}</div>}
          <label>
            Username
            <input
              type="text"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Username"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 4 }}>
            {loading ? <><Loader2 size={16} className="spin" /> Signing in…</> : "Sign in"}
          </button>
          <div className="row" style={{ gap: 8, color: "var(--text-mute)", fontSize: 12, marginTop: 6 }}>
            <ShieldCheck size={14} /> Restricted to authorised Orbit SA staff
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
